import React, { useEffect, useState } from 'react';
import { StarIcon, ZapIcon, ArrowRightIcon } from 'lucide-react';
function useCountdown(initialSeconds: number) {
  const [seconds, setSeconds] = useState(initialSeconds);
  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(s => s > 0 ? s - 1 : 0);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds % 3600 / 60);
  const s = seconds % 60;
  return {
    hours: String(h).padStart(2, '0'),
    minutes: String(m).padStart(2, '0'),
    secs: String(s).padStart(2, '0')
  };
}
export function Hero() {
  const {
    hours,
    minutes,
    secs
  } = useCountdown(23 * 3600 + 47 * 60 + 12);
  const scrollToPrice = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-screen bg-[#1A1A2E] flex items-center overflow-hidden pt-20" data-id="element-75">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: `linear-gradient(#4361EE 1px, transparent 1px), linear-gradient(90deg, #4361EE 1px, transparent 1px)`,
      backgroundSize: '60px 60px'
    }} data-id="element-76" />

      {/* Background glow blobs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#4361EE]/10 rounded-full blur-3xl pointer-events-none" data-id="element-77" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#F72585]/10 rounded-full blur-3xl pointer-events-none" data-id="element-78" />

      <div className="relative max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center w-full" data-id="element-79">
        {/* Left: Text content */}
        <div className="opacity-0-start animate-fade-in-up" data-id="element-80">
          {/* Label */}
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-81">
            <span className="text-[#4361EE] text-xs font-bold tracking-[0.25em] uppercase border border-[#4361EE]/40 px-3 py-1.5 rounded-full" style={{
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-82">
              THE ADHD PRODUCTIVITY SYSTEM
            </span>
          </div>

          {/* Main headline */}
          <h1 className="text-[80px] sm:text-[100px] lg:text-[110px] leading-[0.9] text-white mb-6 glow-text-blue" style={{
          fontFamily: "'Bebas Neue', sans-serif"
        }} data-id="element-83">
            STOP
            <br data-id="element-84" />
            <span className="text-[#4361EE]" data-id="element-85">SURVIVING.</span>
            <br data-id="element-86" />
            START
            <br data-id="element-87" />
            <span className="text-[#F72585]" data-id="element-88">THRIVING.</span>
          </h1>

          {/* Subheadline */}
          <p className="text-[#A0AEC0] text-lg sm:text-xl leading-relaxed mb-8 max-w-lg" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-89">
            The complete ADHD reset system that rewires how you work, focus, and{' '}
            <strong className="text-white" data-id="element-90">
              finally finish what you start.
            </strong>
          </p>

          {/* Stats */}
          <div className="flex flex-wrap gap-6 mb-10" data-id="element-91">
            <div className="flex items-center gap-2" data-id="element-92">
              <div className="flex" data-id="element-93">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 text-[#FFD60A] fill-[#FFD60A]" data-id="element-94" />)}
              </div>
              <span className="text-white font-semibold text-sm" style={{
              fontFamily: "'Inter', sans-serif"
            }} data-id="element-95">
                4.9/5 average rating
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-96">
              <ZapIcon className="w-4 h-4 text-[#4361EE]" data-id="element-97" />
              <span className="text-white font-semibold text-sm" style={{
              fontFamily: "'Inter', sans-serif"
            }} data-id="element-98">
                10,000+ ADHD brains transformed
              </span>
            </div>
          </div>

          {/* CTA Button */}
          <button onClick={scrollToPrice} className="group relative inline-flex items-center gap-3 bg-[#F72585] hover:bg-[#E01070] text-white font-bold text-xl px-10 py-5 rounded-xl transition-all duration-200 animate-pulse-glow mb-4 w-full sm:w-auto justify-center" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-99">
            GET INSTANT ACCESS — $27
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-100" />
          </button>

          {/* Urgency line */}
          <div className="flex items-center gap-2 text-[#FFD60A] font-bold text-sm glow-text-yellow" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-101">
            <ZapIcon className="w-4 h-4" data-id="element-102" />
            <span data-id="element-103">
              Price increases to $97 in{' '}
              <span className="font-display text-lg" style={{
              fontFamily: "'Bebas Neue', sans-serif"
            }} data-id="element-104">
                {hours}:{minutes}:{secs}
              </span>
            </span>
          </div>
        </div>

        {/* Right: Ebook mockup */}
        <div className="flex justify-center lg:justify-end opacity-0-start animate-fade-in delay-400" data-id="element-105">
          <div className="animate-float" data-id="element-106">
            <EbookMockup data-id="element-107" />
          </div>
        </div>
      </div>

      {/* Bottom scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40" data-id="element-108">
        <span className="text-xs text-[#A0AEC0] uppercase tracking-widest" style={{
        fontFamily: "'Inter', sans-serif"
      }} data-id="element-109">
          Scroll
        </span>
        <div className="w-px h-8 bg-gradient-to-b from-[#A0AEC0] to-transparent" data-id="element-110" />
      </div>
    </section>;
}
function EbookMockup() {
  return <div className="relative" style={{
    perspective: '1000px'
  }} data-id="element-111">
      {/* Glow behind book */}
      <div className="absolute inset-0 bg-[#4361EE]/20 blur-3xl rounded-full scale-75 translate-y-8" data-id="element-112" />

      {/* Book container */}
      <div className="relative" style={{
      transform: 'rotateY(-15deg) rotateX(5deg)',
      transformStyle: 'preserve-3d'
    }} data-id="element-113">
        {/* Book cover with real image */}
        <div className="relative w-64 sm:w-72 rounded-r-lg overflow-hidden" style={{
        height: '360px',
        boxShadow: '0 0 40px rgba(67, 97, 238, 0.5), 0 0 80px rgba(67, 97, 238, 0.2), 20px 20px 60px rgba(0,0,0,0.8)'
      }} data-id="element-114">
          {/* Spine */}
          <div className="absolute left-0 top-0 bottom-0 w-6 bg-[#1A1A2E] z-10 flex items-center justify-center" data-id="element-115">
            <span className="text-white text-xs font-bold tracking-widest" style={{
            fontFamily: "'Bebas Neue', sans-serif",
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            transform: 'rotate(180deg)',
            letterSpacing: '0.2em'
          }} data-id="element-116">
              ADHD RESET
            </span>
          </div>

          {/* Cover image */}
          <img src="https://cdn.magicpatterns.com/uploads/gsCtw4kV2C4YVXrS4NG4Nx/IMAGEN_PORTADA_EN_INGLES.jpg" alt="ADHD Reset Ebook Cover" className="absolute inset-0 w-full h-full object-cover" style={{
          marginLeft: '24px',
          width: 'calc(100% - 24px)'
        }} data-id="element-117" />
        </div>

        {/* Book shadow/depth */}
        <div className="absolute top-2 -right-3 w-4 rounded-r-lg bg-[#0A0A1A]" style={{
        height: '360px',
        transform: 'translateZ(-8px)'
      }} data-id="element-118" />
      </div>

      {/* Reflection */}
      <div className="w-64 sm:w-72 h-16 mt-2 rounded-b-lg opacity-20" style={{
      background: 'linear-gradient(to bottom, rgba(67, 97, 238, 0.3), transparent)',
      transform: 'rotateY(-15deg) rotateX(5deg) scaleY(-0.3)',
      transformOrigin: 'top',
      filter: 'blur(4px)'
    }} data-id="element-119" />
    </div>;
}